import { getBrowserTimezone } from '../../../lib/browser-timezone';
import type { ChatAppType } from '../../../queries/chats';

interface BuildNovaRequestContextOptions {
  appType?: ChatAppType;
  accountUuid?: string;
  additionalContext?: Record<string, unknown>;
  classUuid?: string;
}

/**
 * Builds the per-request context shared by new messages and regenerations.
 * Top-level function values are resolved immediately before sending.
 * The provider's appType describes the current client, regardless of the chat's origin.
 */
export async function buildNovaRequestContext({
  accountUuid,
  appType,
  additionalContext = {},
  classUuid,
}: BuildNovaRequestContextOptions): Promise<Record<string, unknown>> {
  const resolvedContext = await resolveContextValues(additionalContext);

  return {
    accountUuid,
    timezone: getBrowserTimezone(),
    ...(classUuid && { classUuid }),
    ...(Object.keys(resolvedContext).length > 0 && {
      ...resolvedContext,
      additionalContext: resolvedContext,
    }),
    appType: appType ?? 'NAVIGATOR',
  };
}

async function resolveContextValues(context: Record<string, unknown>): Promise<Record<string, unknown>> {
  const resolvedEntries = await Promise.all(
    Object.entries(context).map(async ([key, value]) => [key, typeof value === 'function' ? await value() : value]),
  );

  return Object.fromEntries(resolvedEntries);
}
